"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";
import ArrowDownToLineIcon from "@/app/assets/icons/who-is-icm-for/arrow-down-to-line";
import { DirectPaths } from "@/config/routes";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";
import phoneImg from "@/app/assets/images/hero/phone.png";
import portfolioImg from "@/app/assets/images/hero/portfolio.png";
import {
    BanknoteIcon,
    ChartLineIcon,
    HeroGlowBlueMobile,
    HeroGlowBlueWeb,
    HeroGlowOrange,
    HeroStars,
    HeroWaveMobile,
    HeroWaveWeb,
    ShieldCheckIcon,
} from "@/app/assets/icons/hero/icons";

const ROLLING_WORDS = ["Opportunities.", "Growth.", "Possibilities."] as const;

const RollingWord = ({
    className,
    lineClassName,
}: {
    className: string;
    lineClassName: string;
}) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = window.setInterval(
            () => setIndex((i) => (i + 1) % ROLLING_WORDS.length),
            2500,
        );
        return () => window.clearInterval(id);
    }, []);

    return (
        <span className={`relative block overflow-hidden ${className}`}>
            <span
                className="flex flex-col transition-transform duration-700 ease-in-out"
                style={{
                    transform: `translateY(-${(index * 100) / ROLLING_WORDS.length}%)`,
                }}
            >
                {ROLLING_WORDS.map((word) => (
                    <span key={word} className={`block shrink-0 text-brand-primary-500 ${lineClassName}`}>
                        {word}
                    </span>
                ))}
            </span>
        </span>
    );
};

const YieldBadge = () => (
    <div className="flex -rotate-[12.37deg] flex-col items-start gap-2 rounded-2xl border-[1.5px] border-[#E9E9EB] bg-brand-primary-500 px-3 py-2 drop-shadow-[20px_20px_21.05px_rgba(0,0,0,0.15)]">
        <span className="flex size-5 items-center justify-center rounded-[4.5px] bg-[#FEF5EE] p-[4.5px]">
            <BanknoteIcon className="size-3 md:size-[13.44px]" />
        </span>
        <span className="flex items-center justify-center gap-0.5">
            <span className="text-sm font-regular leading-5 text-white">Yield</span>
            <span className="text-sm font-bold leading-5 text-white md:text-[13px] md:leading-[18px]">
                Engine
            </span>
        </span>
    </div>
);

const MtfCard = () => (
    <div className="w-[123px] rotate-[12.08deg] rounded-2xl border-[1.5px] border-[#E9E9EB] bg-[#F0F5FE] p-3 drop-shadow-[20px_20px_21.05px_rgba(0,0,0,0.15)]">
        <div className="flex flex-col items-start gap-2">
            <p className="whitespace-nowrap leading-none">
                <span className="text-lg font-bold leading-6 text-[#0F1016]">12.5</span>
                <span className="text-sm font-semibold leading-5 text-[#767676]"> %</span>
            </p>
            <div className="flex flex-col gap-0.5">
                <p className="text-xs font-bold leading-4">
                    <span className="text-brand-primary-500">Lower MTF</span>
                    <span className="text-[#575757]"> Rates</span>
                </p>
                <p className="text-xs font-medium leading-4 text-[#767676]">
                    Hold positions longer for less.
                </p>
            </div>
        </div>
    </div>
);

const TrustRow = ({ stacked }: { stacked?: boolean }) => (
    <div className="flex flex-col items-start justify-center gap-5">
        <div className="flex items-center gap-2">
            <HeroStars />
            <p className="whitespace-nowrap text-sm font-semibold leading-5 text-white">
                4.7 | 6.87K RATINGS
            </p>
        </div>
        <div className={`flex items-start gap-5 ${stacked ? "flex-col" : "flex-row items-center"}`}>
            <div className="flex items-center gap-2">
                <ShieldCheckIcon />
                <p className="whitespace-nowrap text-sm font-semibold leading-5 text-white">
                    SEBI REGISTERED
                </p>
            </div>
            <div className="flex items-center gap-2">
                <ChartLineIcon />
                <p className="whitespace-nowrap text-sm font-semibold leading-5 text-white">
                    ₹1,350 CR+ ASSETS MANAGED
                </p>
            </div>
        </div>
    </div>
);

/**
 * Homepage hero — Figma desktop 2121:71564 / mobile 2541:30497
 */
const HeroSection = () => {
    const [showAppModal, setShowAppModal] = useState(false);

    const handleDownloadApp = () => {
        void setAnalyticsEvent(DirectPaths.HOME, "Home_Explore_Broking_Fold2");
        setShowAppModal(true);
    };

    return (
        <section
            aria-label="One Platform. Endless Opportunities."
            className="relative h-[828px] w-full overflow-hidden bg-nav-fg md:h-[907px]"
        >
            <h1 className="sr-only">One Platform. Endless Opportunities</h1>
            {/* Waves */}
            <div
                className="pointer-events-none absolute left-[calc(50%+2.5px)] top-[calc(50%-65.5px)] hidden h-[1970px] w-[1957px] -translate-x-1/2 -translate-y-1/2 md:block"
                aria-hidden
            >
                <HeroWaveWeb className="size-full" />
            </div>
            <div
                className="pointer-events-none absolute left-[calc(50%+83px)] top-1/2 h-[1080px] w-[1043px] -translate-x-1/2 -translate-y-1/2 md:hidden"
                aria-hidden
            >
                <HeroWaveMobile className="size-full" />
            </div>

            {/* ── Desktop (1440 artboard) ── */}
            <div className="relative mx-auto hidden h-full w-full max-w-[1440px] md:block">
                <div className="absolute left-[-136px] top-[602px] size-[463px]" aria-hidden>
                    <HeroGlowOrange filterId="hero-glow-orange-web" />
                </div>

                <div className="absolute left-[120px] top-[266px] z-10 flex w-[813px] flex-col items-start gap-[35px]">
                    <div className="flex w-[612px] flex-col items-start gap-10">
                        <div className="flex w-full flex-col items-start gap-6">
                            <p
                                aria-hidden
                                className="flex w-[711px] flex-col items-start gap-2"
                            >
                                <span className="whitespace-nowrap text-[64px] font-bold leading-[78px] text-white">
                                    One Platform.
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="whitespace-nowrap text-[64px] font-bold leading-[78px] text-white">
                                        Endless{" "}
                                    </span>
                                    <RollingWord
                                        className="h-[73px] w-[455px]"
                                        lineClassName="h-[73px] text-[64px] font-bold leading-[73px]"
                                    />
                                </span>
                            </p>
                            <p className="w-full text-2xl font-regular leading-8 text-neutralOpacityLight-80">
                                Invest across Stocks, Mutual Funds, FDs, Digital Gold & Silver, Global
                                Markets, and much more — all in one trusted platform.
                            </p>
                        </div>

                        <div className="flex items-center gap-3.5">
                            <TrackedLink
                                href="/login"
                                event="Home_Get_Started_Fold1"
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-m)] bg-cta-primary px-4 py-2 text-sm font-medium leading-5 text-cta-primary-text no-underline transition-colors hover:bg-cta-primary-hover hover:text-cta-primary-text-hover"
                            >
                                Start Investing
                                <span className="text-cta-primary-icon">
                                    <GrowWealthChevron />
                                </span>
                            </TrackedLink>
                            <button
                                type="button"
                                onClick={handleDownloadApp}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-m)] bg-white px-4 py-2 text-sm font-medium leading-5 text-cta-secondary-text transition-colors hover:bg-cta-secondary-hover"
                            >
                                Download the app
                                <span className="text-cta-secondary-icon">
                                    <ArrowDownToLineIcon />
                                </span>
                            </button>
                        </div>
                    </div>

                    <TrustRow />
                </div>

                <div className="absolute left-[832px] top-[206px] h-[628px] w-[463px]">
                    <div className="absolute left-0 top-[22px] size-[463px]" aria-hidden>
                        <HeroGlowBlueWeb />
                    </div>
                    <div className="absolute left-[93px] top-0 z-[1] w-[274px]">
                        <Image
                            src={phoneImg}
                            alt="InCred Money app portfolio"
                            width={274}
                            height={558}
                            className="w-[274px] object-contain object-top"
                            sizes="274px"
                            priority
                        />
                    </div>
                    <div className="absolute left-[117px] top-[290px] z-[2] w-[254px] shadow-[20px_20px_42.1px_0px_rgba(0,0,0,0.15)]">
                        <Image
                            src={portfolioImg}
                            alt="Portfolio insights"
                            width={254}
                            height={338}
                            className="w-[254px] object-contain object-top"
                            sizes="254px"
                            priority
                        />
                    </div>
                    <div className="absolute left-[21px] top-[95px] z-[3]">
                        <YieldBadge />
                    </div>
                    <div className="absolute left-[317px] top-[95px] z-[4]">
                        <MtfCard />
                    </div>
                </div>
            </div>

            {/* ── Mobile (393 artboard) ── */}
            <div className="relative h-full md:hidden mt-[82px]">
                <div className="absolute left-[-135px] top-[558px] size-[463px]" aria-hidden>
                    <HeroGlowOrange filterId="hero-glow-orange-mobile" />
                </div>

                <div className="absolute left-4 top-[33px] z-10 flex w-[363px] flex-col items-start gap-[35px]">
                    <div className="flex w-[346px] flex-col items-start gap-4">
                        <p
                            aria-hidden
                            className="flex w-full flex-col items-start gap-2"
                        >
                            <span className="whitespace-nowrap text-[30px] font-bold leading-9 text-white">
                                One Platform.
                            </span>
                            <span className="flex w-full items-center gap-2">
                                <span className="whitespace-nowrap text-[30px] font-bold leading-9 text-white">
                                    Endless{" "}
                                </span>
                                <RollingWord
                                    className="h-9 min-w-0 flex-1"
                                    lineClassName="h-9 text-[30px] font-bold leading-9"
                                />
                            </span>
                        </p>
                        <p className="w-full text-base font-regular leading-[22px] text-neutralOpacityLight-80">
                            Invest across Stocks, Mutual Funds, FDs, Digital Gold & Silver, Global
                            Markets, and much more — all in one trusted platform.
                        </p>
                    </div>
                    <TrustRow stacked />
                </div>

                <div className="absolute left-[19px] top-[393px] h-[402px] w-[342px]">
                    <div className="absolute left-[13px] top-0 size-[329px]" aria-hidden>
                        <HeroGlowBlueMobile />
                    </div>
                    <div className="absolute left-[78px] top-0 z-[1] w-[175px]">
                        <Image
                            src={phoneImg}
                            alt="InCred Money app portfolio"
                            width={175}
                            height={357}
                            className="w-[175px] object-contain object-top"
                            sizes="175px"
                            priority
                        />
                    </div>
                    <div className="absolute left-[94px] top-[186px] z-[2] w-[163px] shadow-[20px_20px_42.1px_0px_rgba(0,0,0,0.15)]">
                        <Image
                            src={portfolioImg}
                            alt="Portfolio insights"
                            width={163}
                            height={216}
                            className="w-[163px] object-contain object-top"
                            sizes="163px"
                            priority
                        />
                    </div>
                    <div className="absolute left-0 top-[61px] z-[3]">
                        <YieldBadge />
                    </div>
                </div>

                <div className="absolute left-[240px] top-[454px] z-[4]">
                    <MtfCard />
                </div>
            </div>

            <GetICMAppModal open={showAppModal} onClose={() => setShowAppModal(false)} />
        </section>
    );
};

export default HeroSection;
